import React, { useState, useEffect } from 'react';
import { CloseIcon, SunIcon, MoonIcon, TwitchIcon, YouTubeIcon } from './Icons';

interface SettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

type Theme = 'dark' | 'light';

interface Settings {
  theme: Theme;
  autoRefresh: boolean;
  refreshInterval: number;
  showViewerCount: boolean;
  showUptime: boolean;
}

const defaultSettings: Settings = {
  theme: 'dark',
  autoRefresh: true,
  refreshInterval: 60,
  showViewerCount: true,
  showUptime: true,
};

const STORAGE_KEY = 'stream-tracker-settings';

export const loadSettings = (): Settings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultSettings, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore parsing errors
  }
  return defaultSettings;
};

export const saveSettings = (settings: Settings): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
};

export const applyTheme = (theme: Theme): void => {
  document.documentElement.setAttribute('data-theme', theme);
};

export const Settings: React.FC<SettingsProps> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const updateSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close settings">
            <CloseIcon />
          </button>
        </div>
        <div className="modal-body">
          <div className="setting-group">
            <h3>Appearance</h3>
            <div className="setting-item">
              <div>
                <div className="setting-label">Theme</div>
                <div className="setting-description">Choose your preferred color scheme</div>
              </div>
              <div className="theme-toggle">
                <button
                  className={`btn btn-sm ${settings.theme === 'dark' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('theme', 'dark')}
                  title="Dark theme"
                >
                  <MoonIcon />
                </button>
                <button
                  className={`btn btn-sm ${settings.theme === 'light' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSetting('theme', 'light')}
                  title="Light theme"
                >
                  <SunIcon />
                </button>
              </div>
            </div>
          </div>

          <div className="setting-group">
            <h3>Stream Display</h3>
            <div className="setting-item">
              <div>
                <div className="setting-label">Show viewer count</div>
                <div className="setting-description">Display live viewer count on stream cards</div>
              </div>
              <button
                className={`toggle ${settings.showViewerCount ? 'active' : ''}`}
                onClick={() => updateSetting('showViewerCount', !settings.showViewerCount)}
                aria-label="Toggle viewer count"
              />
            </div>
            <div className="setting-item">
              <div>
                <div className="setting-label">Show uptime</div>
                <div className="setting-description">Display how long the stream has been live</div>
              </div>
              <button
                className={`toggle ${settings.showUptime ? 'active' : ''}`}
                onClick={() => updateSetting('showUptime', !settings.showUptime)}
                aria-label="Toggle uptime display"
              />
            </div>
          </div>

          <div className="setting-group">
            <h3>Auto Refresh</h3>
            <div className="setting-item">
              <div>
                <div className="setting-label">Enable auto refresh</div>
                <div className="setting-description">Automatically refresh stream list</div>
              </div>
              <button
                className={`toggle ${settings.autoRefresh ? 'active' : ''}`}
                onClick={() => updateSetting('autoRefresh', !settings.autoRefresh)}
                aria-label="Toggle auto refresh"
              />
            </div>
            {settings.autoRefresh && (
              <div className="setting-item">
                <div>
                  <div className="setting-label">Refresh interval</div>
                  <div className="setting-description">How often to check for new streams</div>
                </div>
                <select
                  className="sort-select"
                  value={settings.refreshInterval}
                  onChange={(e) => updateSetting('refreshInterval', Number(e.target.value))}
                  aria-label="Refresh interval"
                >
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={120}>2 minutes</option>
                  <option value={300}>5 minutes</option>
                </select>
              </div>
            )}
          </div>

          <div className="setting-group">
            <h3>Connected Accounts</h3>
            <div className="setting-item">
              <div className="flex items-center gap-2">
                <TwitchIcon />
                <span className="setting-label">Twitch</span>
              </div>
              <a href="/api/auth/twitch/login" className="btn btn-sm btn-twitch">
                Connect
              </a>
            </div>
            <div className="setting-item">
              <div className="flex items-center gap-2">
                <YouTubeIcon />
                <span className="setting-label">YouTube</span>
              </div>
              <a href="/api/auth/youtube/login" className="btn btn-sm btn-youtube">
                Connect
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const useSettings = () => {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    applyTheme(settings.theme);
  }, []);

  const updateSettings = (newSettings: Partial<Settings>) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    saveSettings(updated);
    if (newSettings.theme) {
      applyTheme(newSettings.theme);
    }
  };

  return { settings, updateSettings };
};
